// src/pages/Community/TroupePage.tsx
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, Users } from 'lucide-react';
import StudioShell from '../../components/Studio/StudioShell';
import StudioHero from '../../components/Studio/StudioHero';
import MemberCard from '../../components/Studio/MemberCard';
import FilterChips from '../../components/Studio/FilterChips';
import {
  StudioEmptyState,
  StudioErrorState,
  StudioSkeleton,
} from '../../components/Studio/StudioStates';
import {
  CONTAINER,
  INPUT,
  PRIMARY_BUTTON,
  SECONDARY_BUTTON,
} from '../../components/Studio/studioStyles';
import useStudioResource from '../../hooks/useStudioResource';
import useSEO from '../../hooks/useSEO';
import communityService from '../../services/communityService';

const PAGE_SIZE = 24;

/** The craft groupings people actually search by, not all 20 enum values. */
const CRAFT_FILTERS = [
  { value: '', label: 'Everyone' },
  { value: 'ACTOR', label: 'Actors' },
  { value: 'DIRECTOR', label: 'Directors' },
  { value: 'WRITER', label: 'Writers' },
  { value: 'CINEMATOGRAPHER', label: 'Camera' },
  { value: 'SOUND', label: 'Sound' },
  { value: 'EDITOR', label: 'Edit' },
  { value: 'COMPOSER', label: 'Music' },
  { value: 'PRODUCTION_DESIGN', label: 'Design' },
  { value: 'ANIMATOR', label: 'Animation' },
  { value: 'PRODUCER', label: 'Producers' },
];

const TroupePage = () => {
  useSEO({
    title: 'The Troupe — Every StrymHub Member',
    description:
      'The StrymHub repertory: actors, directors, writers, camera, sound, edit, music and design. Every member has their own page, their own credits and their own body of work.',
    keywords:
      'strymhub troupe, sheffield actors, uk film crew, actor directory, crew directory, indie film talent, cast and crew profiles',
    type: 'website',
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const craft = searchParams.get('craft') ?? '';
  const activeSearch = searchParams.get('q') ?? '';

  const [searchInput, setSearchInput] = useState(activeSearch);
  const [page, setPage] = useState(0);

  const members = useStudioResource(
    (signal) =>
      communityService.getMembers(
        {
          craft: craft || undefined,
          search: activeSearch || undefined,
          page,
          size: PAGE_SIZE,
        },
        signal,
      ),
    [craft, activeSearch, page],
  );

  const results = members.data;
  const isFiltered = Boolean(craft || activeSearch);

  const updateParams = (next: { craft?: string; q?: string }) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(next).forEach(([key, value]) => {
      if (value) params.set(key, value);
      else params.delete(key);
    });
    setSearchParams(params, { replace: true });
    setPage(0);
  };

  const handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    updateParams({ q: searchInput.trim() });
  };

  const clearFilters = () => {
    setSearchInput('');
    setSearchParams(new URLSearchParams(), { replace: true });
    setPage(0);
  };

  return (
    <StudioShell>
      <StudioHero
        eyebrow="The StrymHub Community"
        title="The troupe."
        lede="Everyone who makes StrymHub work. Search by craft, by skill or by name — and if you can't find who you need, the next member could be you."
        stats={results ? [{ label: 'Members', value: String(results.totalElements) }] : undefined}
        actions={
          <Link to="/community/join" className={PRIMARY_BUTTON}>
            Join the troupe
          </Link>
        }
      />

      <div className={`${CONTAINER} py-12`}>
        <form onSubmit={handleSearchSubmit} className="mb-8">
          <label htmlFor="troupe-search" className="sr-only">
            Search the troupe by name, skill or location
          </label>
          <div className="relative max-w-lg">
            <Search
              className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-500"
              aria-hidden="true"
            />
            <input
              id="troupe-search"
              type="search"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Search by name, skill or location"
              className={`${INPUT} pl-11`}
            />
          </div>
        </form>

        <FilterChips
          label="Filter the troupe by craft"
          options={CRAFT_FILTERS}
          value={craft}
          onChange={(value) => updateParams({ craft: value })}
        />

        <div className="mt-10">
          {members.loading && <StudioSkeleton count={8} aspect="aspect-[4/5]" />}

          {!members.loading && members.error && (
            <StudioErrorState message={members.error} onRetry={members.reload} />
          )}

          {!members.loading && !members.error && results && results.content.length === 0 && (
            <StudioEmptyState
              icon={<Users className="h-8 w-8" />}
              title={isFiltered ? 'Nobody matches that yet' : 'The troupe is forming'}
              description={
                isFiltered
                  ? "We haven't got anyone in that craft yet — which means there's a spot open."
                  : "We're building the founding repertory right now. Join and you'll be one of the first."
              }
              action={
                isFiltered ? (
                  <div className="flex flex-wrap justify-center gap-3">
                    <button type="button" onClick={clearFilters} className={SECONDARY_BUTTON}>
                      Clear filters
                    </button>
                    <Link to="/community/join" className={PRIMARY_BUTTON}>
                      Join the troupe
                    </Link>
                  </div>
                ) : (
                  <Link to="/community/join" className={PRIMARY_BUTTON}>
                    Become a founding member
                  </Link>
                )
              }
            />
          )}

          {!members.loading && !members.error && results && results.content.length > 0 && (
            <>
              <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4">
                {results.content.map((member) => (
                  <MemberCard key={member.id} member={member} />
                ))}
              </div>

              {results.totalPages > 1 && (
                <nav
                  aria-label="Troupe pagination"
                  className="mt-12 flex items-center justify-center gap-4"
                >
                  <button
                    type="button"
                    onClick={() => setPage((current) => Math.max(0, current - 1))}
                    disabled={results.first}
                    className={SECONDARY_BUTTON}
                  >
                    Previous
                  </button>
                  <p aria-live="polite" className="text-sm text-neutral-400">
                    Page {results.number + 1} of {results.totalPages}
                  </p>
                  <button
                    type="button"
                    onClick={() => setPage((current) => current + 1)}
                    disabled={results.last}
                    className={SECONDARY_BUTTON}
                  >
                    Next
                  </button>
                </nav>
              )}
            </>
          )}
        </div>
      </div>
    </StudioShell>
  );
};

export default TroupePage;
